import { Either } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Запрос списания.
 * @param {string|null} id
 * @param {Product[]} products
 * @param {Date} date
 * @constructor
 */
export function WriteOffRequest(
  id = null,
  products = [],
  date = new Date(),
) {
  this.getId = () => id;
  this.toQuery = () => ({
    products: products.map((product) => ({
      id: product.id,
      price: product.price,
      count: product.count,
    })),
    date,
  });
}

/**
 * @description Товар списания.
 * @param {number} index            Индекс.
 * @param {number} id               Идентификатор товара.
 * @param {string} name             Название товара.
 * @param {number} count            Количество единиц.
 * @param {string} unit             Единица измерения.
 * @param {number} price            Цена.
 * @param {string} countInWarehouse Количество на складе.
 * @constructor
 */
export function Product(
  index,
  id,
  name = '',
  count = 0,
  unit = '',
  price = 0,
  countInWarehouse = 0,
) {
  this.name = name;
  this.id = id;
  this.count = count;
  this.index = index;
  this.unit = unit;
  this.price = price;
  this.countInWarehouse = countInWarehouse;
  const finalPrice = count * price;
  this.getFinalPrice = () => finalPrice;
  this.setPrice = (newPrice) => new this
    .constructor(index, id, name, count, unit, newPrice, countInWarehouse);
  this.setCount = (value) => new this
    .constructor(index, id, name, value, unit, price, countInWarehouse);
}

/**
 * @description Списание.
 * @param {string} id
 * @param {Product[]} products
 * @param {string} createdDateTitle
 * @param {Date} date
 * @constructor
 */
export function WriteOff(
  id,
  products,
  date = new Date(),
  createdDateTitle = '',
) {
  this.id = id;
  this.products = products;
  this.createdDateTitle = createdDateTitle;
  this.date = date;
  const start = JSON.stringify(this);
  /**
   * @description Есть ли изменения.
   * @returns {boolean}
   */
  this.isChanged = () => JSON.stringify(this) !== start;
}

/**
 * @description Шлюз списаний.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function WriteOffGateway(axios) {
  /**
   * @description Просмотр списания.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.viewWriteOff = (id) => axios
    .get(`/api/warehouse/write-offs/${id}`)
    .then(
      async (response) => Either.right({
        writeOff: new WriteOff(
          response.data.id,
          response.data.products.map((item, index) => (new Product(
            index,
            item.product_id,
            item.product_name,
            item.count,
            item.product_unit,
            item.price,
            item.count_in_warehouse,
          ))),
          response.data.created_date_title,
          response.data.created_date_title,
        ),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Новое поступление.
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.create = (params) => axios
    .post('/api/warehouse/write-offs', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удаление списания.
   * @param {string} id Идентификатор списания.
   * @returns {Promise<Either>}
   */
  this.delete = (id) => axios
    .delete(`/api/warehouse/write-offs/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Редактирование списания.
   * @param {string} id
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.edit = (id, params) => axios
    .put(`/api/warehouse/write-offs/${id}`, params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить все списания.
   * @param {number} page Номер страницы.
   * @returns {Promise<Either>}
   */
  this.getAll = (page = 1) => axios
    .get('/api/warehouse/write-offs', { params: { page } })
    .then(
      async (response) => {
        const { data } = response;
        const pageCount = data.page_count;
        const writeOffs = data.writeOffs.map((item) => ({
          id: item.id,
          number: item.number,
          date: item.date,
          amount: item.amount,
          author: item.author,
          products: item.products.map((product, index) => ({
            index,
            name: product.name,
            count: product.count,
            unit: product.unit,
            amount: product.amount,
          })),
        }));
        return Either.right({
          writeOffs,
          pageCount,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Показать опции.
   * @param {string}      search
   * @param {string|null} selectedWriteOffId
   * @returns {Promise<Either>}
   */
  this.viewOptions = (search = '', selectedWriteOffId = null) => axios
    .get('/api/warehouse/write-off/options', { params: { write_off_id: selectedWriteOffId, search } })
    .then(
      async (response) => {
        const products = response.data.products.map((item) => ({
          id: item.id,
          name: item.name,
          unit: item.unit,
          price: item.price,
          countInWarehouse: item.count_in_warehouse,
        }));
        return Either.right({
          products,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
